import React, { FC, ChangeEvent, Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Input, MenuItem, Select } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';

import { UserStatus, UserType } from 'api';
import { Alert } from 'components';

import Invite from './Invite';

interface IProps extends WithStyles<typeof styles> {
  count: number;
  filter: UserStatus;
  tabs: ReactNode;
  search: ReactNode;
  total: ReactNode;
  onUpdateStatus: (value: UserStatus) => void;
  onUpdateType: (value: UserType) => void;
}

const styles = (theme: Theme) =>
  createStyles({
    input: {
      fontWeight: theme.typography.fontWeightBold,
      backgroundColor: theme.palette.grey[300],
    },
    select: {
      // TODO: Work with design on consistency
      padding: '9.5px 72px 9.5px 12px',
    },
  });

const Header: FC<IProps> = props => {
  const { classes, count, filter, tabs, search, total, onUpdateStatus, onUpdateType } = props;

  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [typeConfirmOpen, setTypeConfirmOpen] = useState(false);
  const [actionValue, setActionValue] = useState(null);
  const { t } = useTranslation();

  const canSuspend = filter === UserStatus.Active;
  const canActivate = filter === UserStatus.Disabled;

  function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    const { value } = event.target;

    const isUserStatus = value === UserStatus.Active || value === UserStatus.Disabled;
    const isUserType =
      value === UserType.User || value === UserType.Contributor || value === UserType.Admin;

    if (isUserStatus) {
      setStatusConfirmOpen(true);
    }

    if (isUserType) {
      setTypeConfirmOpen(true);
    }

    setActionValue(value);
  }

  function handleCancel() {
    setStatusConfirmOpen(false);
    setTypeConfirmOpen(false);
    setActionValue(null);
  }

  function renderStatusConfirmation() {
    const status = canSuspend ? t('admin:suspend') : t('admin:activate');

    return (
      <Alert
        open={statusConfirmOpen}
        onConfirm={() => {
          onUpdateStatus(actionValue);
          setStatusConfirmOpen(false);
        }}
        onCancel={handleCancel}
        message={t('admin:userStatusMessage', { count, status })}
        confirm={status}
      />
    );
  }

  function renderTypeConfirmation() {
    return (
      <Alert
        open={typeConfirmOpen}
        onConfirm={() => {
          onUpdateType(actionValue);
          setTypeConfirmOpen(false);
        }}
        onCancel={handleCancel}
        message={t('admin:userTypeMessage', { count, userType: actionValue })}
        confirm={t('common:confirm')}
      />
    );
  }

  function renderAction() {
    if (!onUpdateStatus && !onUpdateType) {
      return <Invite />;
    }

    return (
      <Select
        displayEmpty
        value=""
        classes={{ selectMenu: classes.select }}
        input={<Input className={classes.input} />}
        onChange={handleChange}>
        <MenuItem value="" disabled>
          {t('common:action_plural')}
        </MenuItem>
        {canSuspend && <MenuItem value={UserStatus.Disabled}>{t('admin:suspend')}</MenuItem>}
        {canActivate && <MenuItem value={UserStatus.Active}>{t('admin:activate')}</MenuItem>}
        <MenuItem value={UserType.Admin}>{t('common:admin')}</MenuItem>
        <MenuItem value={UserType.Contributor}>{t('common:contributor')}</MenuItem>
        <MenuItem value={UserType.User}>{t('admin:baseUser')}</MenuItem>
      </Select>
    );
  }

  return (
    <Fragment>
      <Grid container alignItems="center">
        <Grid item xs>
          {tabs}
        </Grid>
        <Grid item>{search}</Grid>
        <Grid item xs>
          <Grid container alignItems="center" justify="flex-end" spacing={3}>
            <Grid item>{total}</Grid>
            <Grid item>{renderAction()}</Grid>
            <Grid item />
          </Grid>
        </Grid>
      </Grid>
      {renderStatusConfirmation()}
      {renderTypeConfirmation()}
    </Fragment>
  );
};

export default withStyles(styles)(Header);
